/**
 * statusVocabulary — every state string in the product maps to a tone in ONE
 * file, so the same state never appears in two different colours on two screens.
 *
 * Contract: docs/design-system/TOKEN_CONTRACT.md §13
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * Measured 2026-09-01: `StatusBadge` had 60 importers and no vocabulary. Each
 * screen wrote its own map, under five different names for one job —
 * `ASSET_STATUS_BADGE`, `BADGE_STATUS`, `STATUS_VARIANT`, `STATUS_MAP`,
 * `STATUS_BADGE` — plus parallel `LABEL_MAP` / `STATUS_LABEL` tables and inline
 * ternary chains. The demo-session states (scheduled/live/ended/cancelled) were
 * declared **three times** in three files, and the compliance states twice.
 *
 * Worse, `status` meant two different things depending on the call site: some
 * passed a STATE (`active`, `pending`) and some passed a TONE (`success`,
 * `default`). The component pasted whichever it got into `ui-badge--${status}`,
 * so a value with no matching class — `success`, `default`, `live` among them —
 * rendered a pill with no colour at all and nothing reported it.
 *
 * ── THE MODEL ───────────────────────────────────────────────────────────────
 *
 * A DOMAIN (`demoSession`, `compliance`, `asset`, …) maps its own enum to a
 * `{ label, tone, pulse }`. A tone is one of seven, and the tone semantics are
 * the product's, not a palette's:
 *
 *   neutral   inert, done, or not started — the default
 *   info      scheduled, queued, or in a normal transitional state
 *   success   completed successfully; nothing to do
 *   warning   needs a human eventually; not broken
 *   danger    failed or blocked; needs a human now
 *   live      happening at this instant. Pulses. Rare by design.
 *   brand     identity or role, not health (an admin role chip)
 *
 * An unknown value resolves to a humanised label at tone `neutral`. It never
 * throws and never renders an unstyled pill — a status the API added last week
 * must degrade to a grey chip, not to invisible.
 */

/** The seven tones. A component may style these and nothing else. */
export const TONES = ["neutral", "info", "success", "warning", "danger", "live", "brand"];

/**
 * ── LEGACY STATUS WORDS ─────────────────────────────────────────────────────
 *
 * The vocabulary the app already speaks, mapped to the tone it ALREADY rendered
 * as. This table is a compatibility layer, not a design: it exists so the 60
 * call sites still passing a bare `status="…"` keep the exact colour they have
 * today while the domains below take over one screen at a time.
 *
 * The groupings are lifted verbatim from the `.ui-badge--*` rules they replace,
 * so nothing changes appearance. The entries at the bottom are the ones that
 * had NO rule and were rendering colourless.
 */
const LEGACY_TONES = {
  // was .ui-badge--active, --ongoing, --placed, --confirmed
  active: "success", ongoing: "success", placed: "success", confirmed: "success",
  // was --completed, --enquired, --contacted
  completed: "info", enquired: "info", contacted: "info",
  // was --pending, --upcoming, --postponed
  pending: "warning", upcoming: "warning", postponed: "warning",
  // was --destructive, --dropped
  destructive: "danger", dropped: "danger",
  // was --new
  new: "brand",
  // was --inactive, --closed
  inactive: "neutral", closed: "neutral",

  // ⚠️ These had no rule at all. A call site passing one got a pill with no
  // background, no border and no colour — visible in the app today wherever
  // `status={x ? 'success' : 'default'}` is written. They are tone names, so
  // they now resolve to themselves.
  success: "success", warning: "warning", danger: "danger", info: "info",
  live: "live", neutral: "neutral", default: "neutral",
};

/** States that mean "right now" and therefore pulse, whatever domain they are in. */
const PULSING = new Set(["live", "ongoing", "in_progress", "running", "active_call"]);

/**
 * ── DOMAINS ─────────────────────────────────────────────────────────────────
 *
 * Only domains whose enum is actually established in the codebase are here.
 * A domain is added when a screen needs it, with its real values — never
 * invented ahead of a caller, because a map nobody reads is a map nobody
 * maintains and it drifts from the API silently.
 */
export const DOMAINS = {
  /**
   * Demo sessions. Was declared three times: DemoClassesListPage,
   * DemoClassesDetailPage and Dashboard/TrainerDashboard/cards/DemoSessionsCard,
   * each with its own parallel label table.
   *
   * ⚠️ `live` was mapped to `ongoing` — the SUCCESS green — in all three, with a
   * `dot` passed alongside that rendered nothing (there has never been a
   * `.ui-badge__dot` rule). A class in progress now uses the `live` tone and
   * actually pulses, which is what those call sites were reaching for.
   */
  demoSession: {
    scheduled: { label: "Scheduled", tone: "info" },
    live: { label: "Live now", tone: "live" },
    ended: { label: "Ended", tone: "neutral" },
    cancelled: { label: "Cancelled", tone: "danger" },
  },

  /**
   * Batch / class sessions — the same shape as a demo, different words.
   * ⚠️ `ongoing` does not pulse: a batch is Ongoing for months, and `live` is
   * the badge that says a class is running right now.
   */
  batch: {
    upcoming: { label: "Upcoming", tone: "info" },
    ongoing: { label: "Ongoing", tone: "success", pulse: false },
    live: { label: "Live now", tone: "live" },
    completed: { label: "Completed", tone: "neutral" },
    cancelled: { label: "Cancelled", tone: "danger" },
    postponed: { label: "Postponed", tone: "warning" },
  },

  /**
   * One day of a batch's day-wise syllabus, as `GET /syllabus/batch/:id/syllabus`
   * reports it to staff (`session.schedule.state`). `overdue` and `today` are
   * read against the date — the stored status of both is `scheduled`.
   * ⚠️ `in_progress` does not pulse: a day can sit in progress for a week.
   */
  syllabusDay: {
    scheduled: { label: "Upcoming", tone: "neutral" },
    // Still to teach, with no date planned yet — a Smart Session added after
    // the batch started. A warning: someone has to plan it.
    undated: { label: "No date yet", tone: "warning" },
    today: { label: "Today", tone: "info" },
    in_progress: { label: "In progress", tone: "info", pulse: false },
    overdue: { label: "Overdue", tone: "warning" },
    completed: { label: "Completed", tone: "success" },
    skipped: { label: "Skipped", tone: "neutral" },
  },

  /** Mandatory-training compliance. Was STATUS_VARIANT, declared twice. */
  compliance: {
    completed: { label: "Completed", tone: "success" },
    in_progress: { label: "In progress", tone: "info" },
    pending: { label: "Pending", tone: "warning" },
    overdue: { label: "Overdue", tone: "danger" },
    exempted: { label: "Exempted", tone: "neutral" },
  },

  /** Asset inventory. Was ASSET_STATUS_BADGE in Assets/assetConstants.js. */
  asset: {
    "in-stock": { label: "In stock", tone: "success" },
    assigned: { label: "Assigned", tone: "success" },
    "under-maintenance": { label: "Under maintenance", tone: "warning" },
    lost: { label: "Lost", tone: "danger" },
    damaged: { label: "Damaged", tone: "danger" },
    retired: { label: "Retired", tone: "neutral" },
    disposed: { label: "Disposed", tone: "neutral" },
    sold: { label: "Sold", tone: "info" },
  },

  /**
   * A reported batch chat message — `ChatReport.status`. `open` is `warning`:
   * an admin has to decide, and App Store Guideline 1.2 wants it within a day.
   * `removed` is the report acted on, so nothing is left to do.
   */
  chatReport: {
    open: { label: "Needs review", tone: "warning" },
    removed: { label: "Removed", tone: "success" },
    dismissed: { label: "Dismissed", tone: "neutral" },
  },

  /**
   * A UPI QR payment a student uploaded proof for — `QrPayment.status`.
   * `submitted` is `warning` for staff (someone must check the bank) and reads
   * "Waiting for approval" to the student, who has nothing to do but wait.
   */
  qrPayment: {
    submitted: { label: "Waiting for approval", tone: "warning" },
    approved: { label: "Approved", tone: "success" },
    rejected: { label: "Rejected", tone: "danger" },
    cancelled: { label: "Withdrawn", tone: "neutral" },
  },

  /**
   * One document a student handed in — `StudentDocument.verification.status`.
   * `pending` is `warning` for the office (someone has to look) and reads
   * "Awaiting check" to the student, who has nothing to do but wait.
   */
  studentDocument: {
    pending: { label: "Awaiting check", tone: "warning" },
    verified: { label: "Verified", tone: "success" },
    rejected: { label: "Rejected", tone: "danger" },
  },

  /**
   * A contract student's evaluation — `StudentEvaluation.status`. Each waiting
   * state is `warning` because a PERSON is waited on; `evaluated` is the office's
   * own next step, so it is `info`.
   */
  studentEvaluation: {
    submitted: { label: "Awaiting review", tone: "warning" },
    returned: { label: "Changes requested", tone: "warning" },
    evaluated: { label: "Agreement drafted", tone: "info" },
    sent: { label: "Awaiting student signature", tone: "warning" },
    student_signed: { label: "Awaiting institute signature", tone: "warning" },
    completed: { label: "Completed", tone: "success" },
    void: { label: "Void", tone: "neutral" },
  },

  /**
   * A course paid for on the public website (Fees → Website orders).
   * `needs_review` is money received that nobody has placed yet — the one
   * state staff must act on, hence `warning`. `expired` is a checkout the
   * visitor never paid, which is why it reads as "Abandoned", not as a failure.
   */
  websiteOrder: {
    fulfilled: { label: "Enrolled", tone: "success" },
    paid: { label: "Enrolling", tone: "info" },
    needs_review: { label: "Needs attention", tone: "warning" },
    refunded: { label: "Refunded", tone: "neutral" },
    created: { label: "Not paid", tone: "neutral" },
    expired: { label: "Abandoned", tone: "neutral" },
  },

  /**
   * Institute subscription state, as shown on the account picker.
   *
   * `trial` is `warning` and not `info`: it is a state that ENDS, and someone
   * has to act before it does.
   */
  institute: {
    active: { label: "Active", tone: "success" },
    trial: { label: "Trial", tone: "warning" },
    suspended: { label: "Suspended", tone: "danger" },
    expired: { label: "Expired", tone: "danger" },
  },

  /**
   * One EMI in a fee plan — the enum on `FeePlan.installments[].status`.
   *
   * `upcoming` and `waived` are `neutral`, not `warning`: neither is anything
   * for the admin to act on, and the legacy word map reads `upcoming` as
   * warning because the only screens using it meant "starting soon".
   */
  feeInstallment: {
    upcoming: { label: "Upcoming", tone: "neutral" },
    due: { label: "Due", tone: "warning" },
    overdue: { label: "Overdue", tone: "danger" },
    partial: { label: "Part paid", tone: "warning" },
    paid: { label: "Paid", tone: "success" },
    waived: { label: "Waived", tone: "neutral" },
  },

  /**
   * An enrollment's fee position. Not stored — derived from a
   * `CourseAssignment`'s paid and pending totals at the call site.
   */
  feeBalance: {
    paid: { label: "Paid", tone: "success" },
    partial: { label: "Partial", tone: "warning" },
    pending: { label: "Pending", tone: "danger" },
  },

  /** One payment — `StudentFee.paymentStatus`. A refund is history, not a task. */
  feePayment: {
    success: { label: "Paid", tone: "success" },
    pending: { label: "Pending", tone: "warning" },
    failed: { label: "Failed", tone: "danger" },
    refunded: { label: "Refunded", tone: "neutral" },
    partially_refunded: { label: "Part refunded", tone: "neutral" },
  },

  /** An invoice issued for a paid subscription — the invoiced states of
   *  `PlanPayment.status`. Unpaid checkouts never reach a list, so there is no
   *  "created" or "failed" here. */
  subscriptionInvoice: {
    paid: { label: "Paid", tone: "success" },
    refunded: { label: "Refunded", tone: "neutral" },
  },

  /** The student record itself — `Student.status`. */
  student: {
    active: { label: "Active", tone: "success" },
    inactive: { label: "Inactive", tone: "neutral" },
  },

  /**
   * The student's LOGIN, which is not their enrolment. Not stored — derived at
   * the call site from the `account` block `GET /students/:id` gives staff.
   * `invited` is `warning`: until they sign up, nothing sent in-app reaches them.
   */
  studentAccount: {
    active: { label: "Login active", tone: "success" },
    invited: { label: "Invite pending", tone: "warning" },
    none: { label: "No login", tone: "neutral" },
  },

  /**
   * One enrolment — `CourseAssignment.status`. `dropped` keeps the danger tone
   * the legacy word map already gives it app-wide; `completed` is done, so it
   * reads like a completed batch.
   */
  enrollment: {
    active: { label: "Active", tone: "success" },
    completed: { label: "Completed", tone: "neutral" },
    dropped: { label: "Dropped", tone: "danger" },
    transferred: { label: "Transferred", tone: "neutral" },
  },

  /**
   * One class, from one student's side — `ZoomAttendance.participants[]
   * .attendanceStatus`. The words are the attendance module's own
   * (AttendanceSessionDetail, DailyAbsenteesPage).
   */
  attendance: {
    present: { label: "Present", tone: "success" },
    partial: { label: "Partial", tone: "warning" },
    absent: { label: "Absent", tone: "danger" },
    dropped: { label: "Dropped", tone: "danger" },
    excused: { label: "Excused", tone: "info" },
    pending: { label: "Not marked", tone: "neutral" },
  },

  /**
   * A placement drive — `Interview.currentStatus`, stored capitalised.
   * ⚠️ `ongoing` sets `pulse: false`: it is a process that runs for days, not a
   * call happening this second, and PULSING would otherwise claim it is live.
   */
  interview: {
    scheduled: { label: "Scheduled", tone: "info" },
    ongoing: { label: "In progress", tone: "info", pulse: false },
    offered: { label: "Offer received", tone: "success" },
    joined: { label: "Joined", tone: "success" },
    rejected: { label: "Rejected", tone: "danger" },
    withdrawn: { label: "Withdrawn", tone: "neutral" },
  },

  /** One round of a drive — `Interview.rounds[].status`, stored capitalised. */
  interviewRound: {
    scheduled: { label: "Scheduled", tone: "info" },
    rescheduled: { label: "Rescheduled", tone: "warning" },
    ongoing: { label: "In progress", tone: "info", pulse: false },
    completed: { label: "Completed", tone: "neutral" },
    passed: { label: "Passed", tone: "success" },
    failed: { label: "Failed", tone: "danger" },
    cancelled: { label: "Cancelled", tone: "neutral" },
  },

  /**
   * An AI mock interview — `MockInterview.status`. One left `in-progress` was
   * abandoned far more often than it is running, so it says so.
   */
  mockInterview: {
    "in-progress": { label: "Not finished", tone: "warning", pulse: false },
    completed: { label: "Completed", tone: "success" },
  },

  /**
   * A public exam — `PublicExam.status`. `published` reads "Live": to the
   * person running a drive it means the link is open to candidates. It does
   * not pulse — a drive stays live for days.
   */
  publicExam: {
    draft: { label: "Draft", tone: "neutral" },
    published: { label: "Live", tone: "success" },
    closed: { label: "Closed", tone: "warning" },
    archived: { label: "Archived", tone: "neutral" },
  },

  /** Someone who signed up through a public exam link — `ExamCandidate.status`. */
  examCandidate: {
    registered: { label: "Signed up", tone: "neutral" },
    verified: { label: "Verified", tone: "info" },
    started: { label: "Started", tone: "info", pulse: false },
    submitted: { label: "Submitted", tone: "success" },
    absent: { label: "Absent", tone: "neutral" },
    disqualified: { label: "Disqualified", tone: "danger" },
  },

  /** One invitation to an invite-only public exam — `ExamInvite.status`. */
  examInvite: {
    pending: { label: "Not sent", tone: "neutral" },
    sent: { label: "Sent", tone: "info" },
    opened: { label: "Opened", tone: "info" },
    used: { label: "Used", tone: "success" },
    revoked: { label: "Revoked", tone: "danger" },
    bounced: { label: "Didn't arrive", tone: "warning" },
  },

  /** A person's decision on a public exam attempt — `integrity.reviewStatus`. */
  examReview: {
    pending: { label: "Not reviewed", tone: "neutral" },
    cleared: { label: "Cleared", tone: "success" },
    disqualified: { label: "Disqualified", tone: "danger" },
  },

  /** A submitted assessment attempt — `AssessmentAttempt.passed`, as a word. */
  assessmentResult: {
    passed: { label: "Passed", tone: "success" },
    failed: { label: "Failed", tone: "danger" },
  },

  /**
   * A support ticket — `SupportTicket.status`. ⚠️ `in_progress` sets
   * `pulse: false`: a ticket is worked on over days, and PULSING would
   * otherwise claim something is happening this second.
   */
  supportTicket: {
    open: { label: "Open", tone: "warning" },
    in_progress: { label: "In progress", tone: "info", pulse: false },
    resolved: { label: "Resolved", tone: "success" },
    closed: { label: "Closed", tone: "neutral" },
  },

  /** A support ticket's urgency — `SupportTicket.priority`. */
  ticketPriority: {
    low: { label: "Low", tone: "neutral" },
    medium: { label: "Medium", tone: "info" },
    high: { label: "High", tone: "warning" },
    critical: { label: "Critical", tone: "danger" },
  },

  /** A batch assignment — `GeneralAssignment.status`. */
  assignment: {
    published: { label: "Open", tone: "success" },
    closed: { label: "Closed", tone: "neutral" },
  },

  /**
   * A batch assignment from the STUDENT's side — `submissionState.state` from
   * GET /general-assignments/student/*. `missed` is past due with nothing handed
   * in and no late work taken.
   */
  assignmentWork: {
    not_submitted: { label: "Not submitted", tone: "neutral" },
    submitted: { label: "Submitted", tone: "info" },
    graded: { label: "Graded", tone: "success" },
    resubmit_requested: { label: "Changes requested", tone: "warning" },
    missed: { label: "Missed", tone: "danger" },
    closed: { label: "Closed", tone: "neutral" },
  },

  /**
   * The same submission from the GRADER's side. `submitted` is a warning here:
   * somebody has to mark it.
   */
  assignmentReview: {
    submitted: { label: "To grade", tone: "warning" },
    graded: { label: "Graded", tone: "success" },
    resubmit_requested: { label: "Sent back", tone: "info" },
    not_submitted: { label: "Not submitted", tone: "neutral" },
  },

  /** A distributor (DistributorProfile.status) — docs/distributors/DISTRIBUTOR_TODO.md. */
  distributor: {
    active: { label: "Active", tone: "success" },
    paused: { label: "Paused", tone: "warning" },
    terminated: { label: "Ended", tone: "neutral" },
  },

  /**
   * One client of a distributor, as the distributor's switch sees it. `held` is
   * the platform's suspension, which the distributor cannot lift — a warning,
   * not a failure: nothing is broken, someone else decides.
   */
  distributorClient: {
    on: { label: "Active", tone: "success" },
    off: { label: "Inactive", tone: "danger" },
    held: { label: "On hold", tone: "warning" },
  },

  /** A distributor's monthly invoice — ServiceInvoice.status. */
  /** A WhatsApp automation message (vc_be WhatsAppAutomationLog.status). "Sent"
   *  is only Meta accepting it — delivery is what succeeds. A skip is the daily
   *  cap, not a fault, so it is `warning`, never `danger`. */
  automationMessage: {
    queued: { label: "Queued", tone: "info" },
    sent: { label: "Sent", tone: "info" },
    delivered: { label: "Delivered", tone: "success" },
    read: { label: "Read", tone: "success" },
    failed: { label: "Failed", tone: "danger" },
    skipped: { label: "Skipped", tone: "warning" },
  },
  /** A WhatsApp template's review state at Meta (WhatsAppTemplate.status). */
  whatsappTemplate: {
    approved: { label: "Approved", tone: "success" },
    pending: { label: "Waiting for Meta", tone: "info" },
    paused: { label: "Paused by Meta", tone: "warning" },
    rejected: { label: "Rejected", tone: "danger" },
    disabled: { label: "Disabled by Meta", tone: "danger" },
  },
  distributorInvoice: {
    draft: { label: "Draft", tone: "neutral" },
    sent: { label: "Sent", tone: "info" },
    viewed: { label: "Viewed", tone: "info" },
    pending_payment: { label: "Awaiting payment", tone: "warning" },
    paid: { label: "Paid", tone: "success" },
    overdue: { label: "Overdue", tone: "danger" },
    cancelled: { label: "Cancelled", tone: "neutral" },
  },
};

/**
 * ── ROLES ARE NOT HEALTH ────────────────────────────────────────────────────
 *
 * A role chip says who someone is, so it is `brand` — never `success`. Its job
 * here is the label: `tenantadmin` must never reach a screen as raw enum text.
 */
export const ROLE_LABELS = {
  tenantadmin: "Platform Admin",
  superadmin: "Super Admin",
  admin: "Admin",
  instructor: "Instructor",
  student: "Student",
};

/** `in_progress` / `under-maintenance` → `In progress` / `Under maintenance`. */
export const humanise = (value) => {
  const s = String(value).replace(/[_-]+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";
};

/**
 * The one lookup. Returns `{ label, tone, pulse }` for any value, always.
 *
 *   resolveStatus("live", "demoSession")  → { label: "Live now", tone: "live", pulse: true }
 *   resolveStatus("active")               → { label: "Active", tone: "success", pulse: false }
 *   resolveStatus("half_refunded")        → { label: "Half refunded", tone: "neutral", pulse: false }
 *
 * @param {string} value   the status from the API, or a legacy tone word
 * @param {string} [domain] a key of DOMAINS; omit to use the legacy table
 */
export const resolveStatus = (value, domain) => {
  if (value === null || value === undefined || value === "") {
    return { label: "", tone: "neutral", pulse: false };
  }
  const raw = String(value);
  const key = raw.toLowerCase();

  const entry = domain && DOMAINS[domain] ? DOMAINS[domain][key] : undefined;
  if (entry) {
    return {
      label: entry.label,
      tone: entry.tone,
      pulse: entry.pulse ?? PULSING.has(key),
    };
  }

  const legacy = LEGACY_TONES[key];
  return {
    // A legacy word IS the label at the call sites that pass one, and the badge
    // has always capitalised it in CSS. An unknown value gets humanised so an
    // enum never reaches a reader with its underscores on.
    label: humanise(raw),
    tone: legacy || "neutral",
    pulse: PULSING.has(key),
  };
};

export default { TONES, DOMAINS, ROLE_LABELS, resolveStatus, humanise };
